import React from 'react';
import * as Clipboard from 'expo-clipboard';
import { openNativeShare, theme, VStack } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';
import { getHostName } from '@order/utils';

import { type PartialLineItem } from '../../../../graphql';
import { useFavoriteLineItemMutation } from '../../graphql/OrderStatus.generated';
import { OrderStatusLineItemCard } from '../OrderStatusLineItemCard';

export const OrderStatusLineItems = (props: OrderStatusLineItemsProps) => {
  const { lineItems } = props;

  const { t } = useLocalizationContext();
  const [{ fetching: isFetchingFavorite }, favoriteLineItem] =
    useFavoriteLineItemMutation();

  const toggleFavorite = React.useCallback(
    async ({ lineItemId, productId, favorited }: ToggleFavoriteType) => {
      if (!productId || !lineItemId) return;
      await favoriteLineItem({
        input: {
          favorited,
          lineItemId,
          productId,
        },
      });
    },
    [favoriteLineItem],
  );

  const handleShareFavoritePress = React.useCallback(
    (url: string, name: string) => {
      const nativeText = t('general.share-favorite', { productName: name });

      void openNativeShare({
        message: nativeText,
        url,
        options: {
          subject: nativeText,
        },
      });
      void Clipboard.setStringAsync(url);
    },
    [t],
  );

  return (
    <VStack
      gap={0}
      hasDivider={true}
      dividerColor={theme.colors.LIGHT}
      testID="order-status-details-line-items"
    >
      {lineItems.map((lineItem) => {
        const url = `${getHostName()}/share/${lineItem.slug}`;

        return (
          <OrderStatusLineItemCard
            key={lineItem.id ?? lineItem.product.name}
            lineItem={lineItem}
            onFavorite={toggleFavorite}
            isFetchingFavorite={isFetchingFavorite}
            isFavorite={lineItem.favorited}
            onShare={handleShareFavoritePress}
            shareBtnLabel={t('order-status-info.share-product')}
            shareTooltipLabel={t('order-status-info.share-product-tooltip')}
            shareUrl={url}
          />
        );
      })}
    </VStack>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ToggleFavoriteType = Readonly<{
  lineItemId?: string;
  productId?: string;
  favorited: boolean;
}>;

type OrderStatusLineItemsProps = Readonly<{
  lineItems: readonly PartialLineItem[];
}>;
